import { Spinner } from "@hackthenorth/north";
import { ArrowRightIcon } from "@heroicons/react/24/solid";
import React, { useState } from "react";
import styled from "styled-components";

import { UnstyledLink } from "src/shared/components/Link";
import { BaseRoute } from "src/shared/constants/route";

import {
  capDashboardEventSections,
  DashboardScheduleEvent,
  formatCompactDuration,
} from "./dashboardUtils";
import type { TEvent } from "./schedule/Calendar";
import EventModal from "./schedule/EventModal";

const SCHEDULE_EVENT_TYPES = [
  "ceremonies",
  "registration",
  "Main Event",
  "Food",
  "Workshop",
  "Activity",
  "Meetup",
  "Sponsor",
  "Tech Talk",
  "Panel",
  "Judging",
  "Sponsored Workshop",
];

const toModalEvent = (event: DashboardScheduleEvent): TEvent => ({
  title: event.title,
  location: event.location,
  start_time: new Date(event.start_time),
  end_time: new Date(event.end_time),
  type:
    event.tags.find((tag) => SCHEDULE_EVENT_TYPES.includes(tag)) ??
    "Main Event",
  description: event.description ?? "",
  leads: [],
  resources: event.links ?? [],
  row: 0,
});

type EventOverviewProps = {
  happeningNow: DashboardScheduleEvent[];
  hasError: boolean;
  isLoading: boolean;
  now: Date;
  upNext: DashboardScheduleEvent[];
};

type EventRowsProps = {
  events: DashboardScheduleEvent[];
  isHappeningNow: boolean;
  now: Date;
  onSelectEvent: (event: DashboardScheduleEvent) => void;
};

const EventRows: React.FC<EventRowsProps> = ({
  events,
  isHappeningNow,
  now,
  onSelectEvent,
}) => {
  if (events.length === 0) {
    return (
      <EmptyMessage>
        {isHappeningNow
          ? "No events are happening right now."
          : "There are no more upcoming events."}
      </EmptyMessage>
    );
  }

  return (
    <EventList>
      {events.map((event) => {
        const targetTime = new Date(
          isHappeningNow ? event.end_time : event.start_time
        ).getTime();
        const timing = formatCompactDuration(targetTime - now.getTime());

        return (
          <EventRow
            key={event.id}
            type="button"
            onClick={() => onSelectEvent(event)}
          >
            <EventIdentity>
              <EventMarker isHappeningNow={isHappeningNow} />
              <EventTitle data-event-title title={event.title}>
                {event.title}
              </EventTitle>
            </EventIdentity>
            <EventMeta>
              {event.location && (
                <>
                  <EventLocation title={event.location}>
                    {event.location}
                  </EventLocation>
                  <EventMetaSeparator aria-hidden="true">·</EventMetaSeparator>
                </>
              )}
              <EventTiming>
                {isHappeningNow ? `${timing} left` : `in ${timing}`}
              </EventTiming>
            </EventMeta>
          </EventRow>
        );
      })}
    </EventList>
  );
};

const EventOverview: React.FC<EventOverviewProps> = ({
  happeningNow,
  hasError,
  isLoading,
  now,
  upNext,
}) => {
  const [selectedEvent, setSelectedEvent] = useState<TEvent | null>(null);
  const {
    visibleHappeningNow,
    visibleUpNext,
    hiddenHappeningNowCount,
    hiddenUpNextCount,
  } = capDashboardEventSections(happeningNow, upNext);

  return (
    <>
      <EventCard>
        <EventHeader>
          <SectionLabel>Happening now</SectionLabel>
          <ScheduleLink href={BaseRoute.SCHEDULE}>
            Schedule
            <ArrowRightIcon aria-hidden="true" />
          </ScheduleLink>
        </EventHeader>
        {isLoading ? (
          <StateMessage>
            <Spinner />
            Loading the schedule...
          </StateMessage>
        ) : hasError ? (
          <StateMessage role="status">
            The schedule is temporarily unavailable.
          </StateMessage>
        ) : (
          <>
            <EventSectionContainer>
              <EventRows
                events={visibleHappeningNow}
                isHappeningNow
                now={now}
                onSelectEvent={(event) => setSelectedEvent(toModalEvent(event))}
              />
              {hiddenHappeningNowCount > 0 && (
                <MoreLink href={BaseRoute.SCHEDULE}>
                  + {hiddenHappeningNowCount} more...
                </MoreLink>
              )}
            </EventSectionContainer>
            <EventSectionContainer>
              <SectionLabel>Up next</SectionLabel>
              <EventRows
                events={visibleUpNext}
                isHappeningNow={false}
                now={now}
                onSelectEvent={(event) => setSelectedEvent(toModalEvent(event))}
              />
              {hiddenUpNextCount > 0 && (
                <MoreLink href={BaseRoute.SCHEDULE}>
                  + {hiddenUpNextCount} more...
                </MoreLink>
              )}
            </EventSectionContainer>
          </>
        )}
      </EventCard>
      {selectedEvent && (
        <EventModal
          isOpen
          onClose={() => setSelectedEvent(null)}
          event={selectedEvent}
        />
      )}
    </>
  );
};

export default EventOverview;

const EventCard = styled.section`
  display: flex;
  flex: 1 1 720px;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  padding: 16px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.white};

  ${({ theme }) => theme.mediaQueries.medium`
    flex: none;
    width: 100%;
    max-width: none;
  `}

  ${({ theme }) => theme.mediaQueries.tablet`
    padding: 16px 0;
    border: 0;
    border-radius: 0;
  `}
`;

const EventHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const SectionLabel = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: 0.8px;
  text-transform: uppercase;
`;

const ScheduleLink = styled(UnstyledLink)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  text-decoration: none;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;

const EventSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const EventList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const EventRow = styled.button`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 20px;
  width: 100%;
  min-width: 0;
  min-height: 22px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:hover [data-event-title],
  &:focus-visible [data-event-title] {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme }) => theme.globalConstants.color.brandPrimary};
    outline-offset: 2px;
  }

  ${({ theme }) => theme.mediaQueries.largeMobile`
    grid-template-columns: minmax(0, 3fr) minmax(80px, 2fr);
    gap: 12px;
  `}
`;

const EventIdentity = styled.div`
  display: grid;
  grid-template-columns: 3px minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const EventMarker = styled.span<{ isHappeningNow: boolean }>`
  display: block;
  width: 3px;
  height: 22px;
  border-radius: 2px;
  background: ${({ isHappeningNow }) =>
    isHappeningNow ? "#66bee4" : "#d1d5db"};
`;

const EventTitle = styled.span`
  min-width: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 500;
  line-height: normal;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EventMeta = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  white-space: nowrap;

  ${({ theme }) => theme.mediaQueries.mobile`
    font-size: 12px;
  `}
`;

const EventLocation = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EventMetaSeparator = styled.span`
  flex: 0 0 auto;
`;

const EventTiming = styled.span`
  flex: 0 0 auto;
`;

const MoreLink = styled(UnstyledLink)`
  align-self: flex-end;
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  text-decoration: underline;
`;

const EmptyMessage = styled.p`
  min-height: 22px;
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
`;

const StateMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 120px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 14px;
`;
